// Mock data for Bluebloodfr portfolio (frontend-only).
// All content is centralized here to make backend integration easy later.

export const profile = {
  name: "Louis-Melchior Giraud",
  alias: "Bluebloodfr",
  title: "Data Scientist & AI Engineer",
  tagline: "I build reliable AI, data and software solutions for real-world use cases.",
  location: "Luxembourg / France",
  email: "louismelchiorgiraud@gmail.com",
  emailPro: "louismelchiorgiraud@icloud.com",
  github: "https://github.com/Bluebloodfr",
  githubHandle: "Bluebloodfr",
  linkedin: "https://www.linkedin.com/in/louis-melchior-giraud",
  linkedinHandle: "Louis-Melchior Giraud",
  logo: "https://customer-assets.emergentagent.com/job_11f001ce-364e-4b4b-8e7b-fe11463729c3/artifacts/veoyh3rp_LOGO.png",
};

export const aboutText = `I am Louis-Melchior Giraud, a young Data Scientist and AI Engineer with experience in Earth Observation, machine learning, computer vision, dashboarding and full-stack development. I combine technical skills, clear communication and a practical mindset to build tools that are useful, understandable and production-oriented.`;

export const aboutHighlights = [
  { label: "Currently", value: "Data Scientist @ Thales Alenia Space Luxembourg (since Dec 2025)" },
  { label: "Education", value: "Data Engineering & AI @ ESILV" },
  { label: "Past Experience", value: "Ministère des Armées / Marine Nationale" },
  { label: "Also", value: "Software development & video editing" },
];

export const trustedBy = [
  {
    name: "Thales Alenia Space",
    logo: "https://customer-assets.emergentagent.com/job_11f001ce-364e-4b4b-8e7b-fe11463729c3/artifacts/rosarq3n_Thales-LOGO-WHITE-ON-BLUE-RGB.png.webp",
    placeholder: false,
  },
  {
    name: "Ministère des Armées",
    logo: "https://customer-assets.emergentagent.com/job_11f001ce-364e-4b4b-8e7b-fe11463729c3/artifacts/ul60ev60_logo_ministe%CC%80re_arme%CC%81es.jpg",
    placeholder: false,
  },
  { name: "More soon", logo: null, placeholder: true },
  { name: "More soon", logo: null, placeholder: true },
];

export const expertise = [
  {
    icon: "BrainCircuit",
    title: "Artificial Intelligence",
    description: "Designing AI systems that are robust, useful and aligned with real business outcomes.",
  },
  {
    icon: "Network",
    title: "Machine Learning",
    description: "Classical ML and deep learning models — from prototyping to deployment-ready code.",
  },
  {
    icon: "Eye",
    title: "Computer Vision",
    description: "Image segmentation, classification and detection on satellite and operational imagery.",
  },
  {
    icon: "Satellite",
    title: "Earth Observation",
    description: "Geospatial pipelines for land-cover, cloud and high-resolution remote sensing tasks.",
  },
  {
    icon: "Database",
    title: "Data Engineering",
    description: "Reliable pipelines: SharePoint, SQL, geospatial formats and reproducible workflows.",
  },
  {
    icon: "BarChart3",
    title: "Dashboards & DataViz",
    description: "Qlik dashboards and custom visualizations that turn raw data into clear decisions.",
  },
  {
    icon: "Code2",
    title: "Full-stack Development",
    description: "Modern web apps with React, Angular, Node.js, FastAPI and clean APIs.",
  },
  {
    icon: "Video",
    title: "Video & Communication",
    description: "Technical video content and demos to make complex projects understandable.",
  },
];

export const projects = [
  {
    id: "reid-graphic-assets",
    title: "Re-Identification of Graphic Assets",
    description:
      "Deep learning model combined with a knowledge graph to re-identify graphic assets across visual collections. Bridges computer vision feature extraction with structured knowledge for robust matching.",
    image:
      "https://images.unsplash.com/photo-1664854953181-b12e6dda8b7c?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85",
    stack: ["PyTorch", "Knowledge Graph", "Python"],
    tags: ["Computer Vision", "Deep Learning"],
    link: "https://github.com/Bluebloodfr",
  },
  {
    id: "drug-consumption",
    title: "Drug Consumption Prediction",
    description:
      "Machine learning pipeline to predict drug consumption from socio-demographic and behavioral features, with model comparison, evaluation and interpretability.",
    image:
      "https://images.unsplash.com/photo-1768403305881-a7a82fd63512?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85",
    stack: ["Scikit-learn", "Python", "Pandas"],
    tags: ["Machine Learning", "Health Data"],
    link: "https://github.com/Bluebloodfr",
  },
  {
    id: "land-cover",
    title: "High-resolution land-cover segmentation",
    description:
      "Prototyped semantic segmentation models on high-resolution satellite imagery to produce detailed land-cover maps for Earth Observation use cases.",
    image:
      "https://images.unsplash.com/photo-1634176866089-b633f4aec882?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85",
    stack: ["PyTorch", "GDAL", "rasterio", "Python"],
    tags: ["Computer Vision", "Earth Observation"],
    link: "https://github.com/Bluebloodfr",
  },
  {
    id: "cloud-seg",
    title: "Cloud segmentation benchmarking",
    description:
      "Benchmarked multiple deep learning architectures for cloud segmentation on multispectral imagery, measuring quality and inference cost.",
    image:
      "https://images.unsplash.com/photo-1563224699-838b293e0af5?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85",
    stack: ["PyTorch", "TensorFlow", "Python"],
    tags: ["Benchmark", "Computer Vision"],
    link: "https://github.com/Bluebloodfr",
  },
  {
    id: "terramind",
    title: "TerraMind foundation model adaptation",
    description:
      "Adapted the TerraMind foundation model to specific Earth Observation downstream tasks, with a focus on transfer learning and evaluation.",
    image:
      "https://images.unsplash.com/photo-1644088379091-d574269d422f?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85",
    stack: ["PyTorch", "Foundation Models", "Python"],
    tags: ["Foundation Model", "Research"],
    link: "https://github.com/Bluebloodfr",
  },
  {
    id: "xai-viz",
    title: "Explainable AI visualizations",
    description:
      "Built explainability and visualization tools to help stakeholders understand model predictions on geospatial and tabular data.",
    image:
      "https://images.unsplash.com/photo-1666875753105-c63a6f3bdc86?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85",
    stack: ["SHAP", "Python", "Plotly"],
    tags: ["Explainability", "Visualization"],
    link: "https://github.com/Bluebloodfr",
  },
  {
    id: "qlik-dashboard",
    title: "Qlik dashboard for operational monitoring",
    description:
      "Designed and built a Qlik Sense dashboard from SharePoint data sources to monitor operational KPIs for the Marine Nationale.",
    image:
      "https://images.unsplash.com/photo-1551288049-bebda4e38f71?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85",
    stack: ["Qlik Sense", "SharePoint", "SQL"],
    tags: ["Dashboard", "BI"],
    link: "https://github.com/Bluebloodfr",
  },
  {
    id: "attrition",
    title: "Attrition prediction with machine learning",
    description:
      "Built ML models (SVM, CatBoost) to predict early resignation, with SHAP-based explanations and a clean, decision-oriented reporting layer.",
    image:
      "https://images.unsplash.com/photo-1557562645-4eee56b29bc1?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85",
    stack: ["CatBoost", "Scikit-learn", "SHAP"],
    tags: ["ML", "HR Analytics"],
    link: "https://github.com/Bluebloodfr",
  },
  {
    id: "language-app",
    title: "Anki-like language learning app",
    description:
      "Personal full-stack project: a spaced-repetition language learning app inspired by Anki with a modern UX and clean architecture.",
    image:
      "https://images.unsplash.com/photo-1673515334669-1e445e4f4c3f?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85",
    stack: ["TypeScript", "Angular", "Node.js"],
    tags: ["Full-stack", "Personal Project"],
    link: "https://github.com/Bluebloodfr",
  },
  {
    id: "clothes-renting",
    title: "Clothes renting full-stack app",
    description:
      "End-to-end full-stack web application for clothes renting: authentication, catalog, booking flow and admin dashboard.",
    image:
      "https://images.unsplash.com/photo-1539278383962-a7774385fa02?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85",
    stack: ["TypeScript", "Node.js", "SQL"],
    tags: ["Full-stack", "Web App"],
    link: "https://github.com/Bluebloodfr",
  },
];

export const experiences = [
  {
    year: "Dec 2025 — present",
    role: "Data Scientist",
    company: "Thales Alenia Space — Luxembourg",
    summary:
      "Working on production-oriented data science projects: real-world machine learning applications, data-driven decision support, robust and explainable models. Some projects are confidential and involve concrete business use cases and operational constraints.",
    tags: ["Data Science", "ML", "Production"],
  },
  {
    year: "2025 — 5 months",
    role: "Software Solutions Technician",
    company: "Thales Alenia Space — Luxembourg",
    summary:
      "High-resolution land-cover segmentation (U-Net + EfficientNet-B4), cloud segmentation benchmarking (OmniCloudMask, Dynamic World), TerraMind foundation model adaptation for multi-modal Earth Observation, explainable AI and uncertainty visualization tools. Collaborated with the GeoPulse team on rapid prototyping and deployment.",
    tags: ["Earth Observation", "Computer Vision", "Foundation Models"],
  },
  {
    year: "2024 — 4 months",
    role: "Data Scientist & ML Engineer Intern",
    company: "Ministère des Armées / Marine Nationale — Paris",
    summary:
      "Built a Qlik dashboard with SharePoint API integration, data transformation and iterative co-design with end-users. Developed attrition prediction models (SVM, CatBoost, PyTorch) with feature engineering, SHAP-based interpretability and decision-oriented visualizations.",
    tags: ["ML", "Dashboards", "HR Analytics"],
  },
];

export const skills = {
  "AI / ML": ["Python", "PyTorch", "TensorFlow", "Scikit-learn", "CatBoost", "SHAP"],
  "Geospatial": ["GDAL", "rasterio", "QGIS"],
  "Data & DB": ["SQL", "PostgreSQL", "MySQL", "Qlik"],
  "Web & Backend": ["TypeScript", "Node.js", "Angular", "HTML", "CSS"],
  "Software Eng.": ["C", "C++", "Java", "C#", "Docker"],
  "Tooling": ["GitHub", "Vercel", "Jira", "MCP protocols"],
};

export const projectTypes = [
  "AI prototype",
  "Computer Vision",
  "Dashboard / BI",
  "Data pipeline",
  "Automation",
  "Full-stack web app",
  "Technical video / demo",
  "AI Coaching / Cours IA",
  "Other",
];

// ===================== RECOMMENDATIONS / TESTIMONIALS =====================

// Featured letter of recommendation (static, kept here for full-text modal)
export const featuredRecommendation = {
  id: "francois-robinet",
  display_name: "François Robinet",
  role: "Data Science Chapter Lead — Thales Alenia Space",
  relationship: "Supervisor",
  quote:
    "Louis-Melchior demonstrated technical skills typically seen in employees with several years of experience, as well as a business acumen that many never fully develop in their careers. He far exceeded expectations, showcasing both remarkable human and technical abilities.",
  date: "June 16, 2025",
  pdfUrl: "/recommendation-francois-robinet.pdf",
  linkedin: "",
  fullText: `Dear admission committee,

I had the pleasure of supervising Louis-Melchior's internship at Thales Alenia Space Luxembourg last semester. His work within TAS focused on the development of novel approaches to cloud and landcover segmentation. Louis-Melchior helped conduct a thorough review of the state-of-the-art, and led the implementation of novel cloud landcover segmentation models based on modern geospatial foundation models.

During his time with us, Louis-Melchior demonstrated technical skills typically seen in employees with several years of experience, as well as a business acumen that many never fully develop in their careers. Louis-Melchior quickly proved to be highly versatile, showing a strong eagerness to learn and an impressive ability to grasp new concepts quickly. This allowed him to start contributing to the team's work right from the start. A rarity among interns, Louis-Melchior was able to work autonomously early on, displaying initiative and consistently asking the right questions.

On a personal level, Louis-Melchior quickly integrated himself into the team and the organization as a whole. His cheerful and easy-going personality was appreciated by all of his colleagues, and he displayed a healthy self-confidence that never crossed into arrogance.

Throughout this internship, Louis-Melchior has far exceeded expectations, showcasing both remarkable human and technical abilities. I am confident that these qualities will continue to develop as he progresses, and I have no doubt he will be a great fit for your program.

I highly recommend that you accept his application.

Sincerely yours,
François Robinet`,
};

export const RELATIONSHIPS = [
  "Colleague",
  "Client",
  "Mentee",
  "Professor",
  "Recruiter",
  "Partner",
  "Other",
];
